import { useState } from "react";
import { useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import { useAuth } from "../../context/AuthContext";
import Toast from "../../helpers/Toast";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import { CompleteAssistanceExecutionDTO } from "../../models/AssistanceExecution/CompleteAssistanceExecutionDTO";
import { CreateAssistanceExecutionDTO } from "../../models/AssistanceExecution/CreateAssistanceExecutionDTO";
import { AssistanceService } from "../../services/AssistanceService";

type ExecutionButtonsType = {
    assistance: AssistanceDTO;
    setAssistance: React.Dispatch<React.SetStateAction<AssistanceDTO>>;
    reloadExecutions: () => void;
    onFetchExecutionsData: () => void;
    isActiveLocation: boolean;
}

export function ExecutionButtons({ assistance, setAssistance, reloadExecutions, isActiveLocation, ...props }: ExecutionButtonsType) {
    // Página
    const intl = useIntl();
    const [isLoadingExecutionButtons, setIsLoadingExecutionButtons] = useState<boolean>(false);
    const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);
    const assistanceService = new AssistanceService();
    const { currentUser } = useAuth();

    // Função para obter localização atual
    const getCurrentLocation = (): Promise<{ latitude: number; longitude: number }> => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                Toast.Show("error", intl.formatMessage({ id: "Screens.AssistanceDetails.GeolocationNotSupported" }));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    resolve({ latitude, longitude });
                },
                (error) => {
                    console.error(error);
                    Toast.Show("error", intl.formatMessage({ id: "Screens.AssistanceDetails.LocationError" }));
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0,
                }
            );
        });
    };

    const startExecution = async () => {
        setIsLoadingExecutionButtons(true);
        if (isActiveLocation) setIsGettingLocation(true);

        if (currentUser?.id == null) {
            Toast.Show("error", intl.formatMessage({ id: "Sreens.ExcutionButton.NoSession" }));
            setIsLoadingExecutionButtons(false);
            if (isActiveLocation) setIsGettingLocation(false);
            return;
        }

        try {
            let executionInfo: CreateAssistanceExecutionDTO = {
                executionStart: new Date(),
                assistanceId: assistance.id,
                technicianId: currentUser.id
            };

            if (isActiveLocation) {
                Toast.Show("info", intl.formatMessage({ id: "Screens.AssistanceDetails.GettingLocation" }));
                const location = await getCurrentLocation();
                executionInfo.startLocationLat = location.latitude;
                executionInfo.startLocationLon = location.longitude;
            }

            var response = await assistanceService.AddExecution(executionInfo);

            setIsLoadingExecutionButtons(false);
            if (isActiveLocation) setIsGettingLocation(false);

            if (response.success !== true || response.obj == null) {
                Toast.Show("error", response.message);
                return;
            }

            setAssistance({
                ...assistance,
                canCompleteExecution: !assistance.canCompleteExecution,
                canStartExecution: !assistance.canStartExecution,
                openExecutionIds: response.obj
            });

            props.onFetchExecutionsData();
            Toast.Show("success", intl.formatMessage({ id: "Screens.ExcutionButton.SessionStarted" }));
        } catch (error) {
            setIsLoadingExecutionButtons(false);
            if (isActiveLocation) setIsGettingLocation(false);
            Toast.Show("error", intl.formatMessage({ id: isActiveLocation ? "Screens.ExcutionButton.LocationError" : "Screens.ExcutionButton.SessionError" }));
        }
    };

    const completeExecution = async () => {
        setIsLoadingExecutionButtons(true);
        if (isActiveLocation) setIsGettingLocation(true);

        if (currentUser?.id == null) {
            Toast.Show("error", "Utilizador não tem sessão iniciada");
            setIsLoadingExecutionButtons(false);
            if (isActiveLocation) setIsGettingLocation(false);
            return;
        }

        try {
            let executionInfo: CompleteAssistanceExecutionDTO = {
                executionEnd: new Date(),
                assistanceExecutionIds: assistance.openExecutionIds ?? 0,
                technicianId: currentUser.id
            };

            if (isActiveLocation) {
                Toast.Show("info", intl.formatMessage({ id: "Screens.AssistanceDetails.GettingLocation" }));
                const location = await getCurrentLocation();
                executionInfo.endLocationLat = location.latitude;
                executionInfo.endLocationLon = location.longitude;
            }

            var response = await assistanceService.CompleteExecution(executionInfo);

            setIsLoadingExecutionButtons(false);
            if (isActiveLocation) setIsGettingLocation(false);

            if (response.success !== true) {
                Toast.Show("error", response.message);
                return;
            }

            setAssistance({
                ...assistance,
                canCompleteExecution: !assistance.canCompleteExecution,
                canStartExecution: !assistance.canStartExecution
            });

            props.onFetchExecutionsData();
            Toast.Show("success", intl.formatMessage({ id: isActiveLocation ? "Screens.ExcutionButton.LocationSuccess" : "Screens.ExcutionButton.FinishSuccess" }));
        } catch (error) {
            setIsLoadingExecutionButtons(false);
            if (isActiveLocation) setIsGettingLocation(false);
            Toast.Show("error", intl.formatMessage({ id: isActiveLocation ? "Screens.ExcutionButton.FinishLocationError" : "Screens.ExcutionButton.SessionError" }));
        }
    };

    return (
        <Row style={{ margin: "15px 2px 10px 0px" }}>
            <Col xl={3} lg={5} md={6} sm={6} xs={6}>
                <ButtonComponent
                    disabled={assistance.canCompleteExecution || isGettingLocation}
                    loading={isLoadingExecutionButtons}
                    text={
                        isGettingLocation
                            ? "Obtendo localização..."
                            : intl.formatMessage({ id: "Screens.Assistances.ExecutionButtons.StartExecution" })
                    }
                    onClick={startExecution}
                    style={{
                        width: "100%",
                        height: "65px",
                        fontSize: "var(--header-font-size)",
                        whiteSpace: "pre-wrap",
                        backgroundColor: isGettingLocation ? "#ccc" : undefined
                    }}
                />
            </Col>

            <Col xl={3} lg={5} md={6} sm={6} xs={6} className={"offset-lg-1 offset-xl-0"}>
                <ButtonComponent
                    disabled={assistance.canStartExecution || isGettingLocation}
                    loading={isLoadingExecutionButtons}
                    text={
                        isGettingLocation
                            ? "Obtendo localização..."
                            : intl.formatMessage({ id: "Screens.Assistances.ExecutionButtons.CompleteExecution" })
                    }
                    onClick={completeExecution}
                    style={{
                        width: "100%",
                        height: "65px",
                        fontSize: "var(--header-font-size)",
                        whiteSpace: "pre-wrap",
                        backgroundColor: isGettingLocation ? "#ccc" : undefined
                    }}
                />
            </Col>
        </Row>
    );
}